<template>
  <div v-if="clientId" class="google-auth">
    <div ref="buttonHost" class="google-auth__button"></div>
    <!-- Immediate click feedback: the GIS button is a Google iframe we
         can't restyle, and its popup can take seconds - without this,
         users click repeatedly. -->
    <div v-if="busy" class="google-auth__status">Signing you in…</div>
    <div v-else-if="waiting" class="google-auth__status">
      Waiting for Google — a popup should be open…
    </div>
    <v-alert v-if="errorMessage" type="error" dense text class="mt-2 mb-0">{{
      errorMessage
    }}</v-alert>
    <div v-if="divider" class="google-auth__divider">
      <span
        >or {{ mode === "register" ? "register" : "log in" }} with email</span
      >
    </div>
  </div>
</template>

<script>
// "Continue with Google" - shared across every app. Renders only when the
// current site exposes a client id via /public-config (set per app with
// SITE_<SLUG>_GOOGLE_CLIENT_ID), so apps without a Google Cloud project
// never show it and ship zero code for it. The credential goes to
// /auth/google, which signs in, links or creates the per-site account;
// then the same post-login continuation as the password forms runs
// (?next= funnel included).
import { takeAuthNext } from "@shared/utils/memberFunnel";

let gisScriptPromise = null;
let oneTapShown = false;

function loadGis() {
  if (window.google && window.google.accounts) return Promise.resolve();
  if (!gisScriptPromise) {
    gisScriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://accounts.google.com/gsi/client";
      s.async = true;
      s.defer = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return gisScriptPromise;
}

export default {
  name: "GoogleAuthButton",
  props: {
    // "login" | "register" - only affects wording (the flow is identical).
    mode: { type: String, default: "login" },
    // Referral code to bind on first-time Google signups.
    refToken: { type: String, default: "" },
    // Also fire One Tap (further gated by the per-site one_tap flag).
    oneTap: { type: Boolean, default: false },
    // Inline placements (e.g. an in-page offer) drop the "or ... with
    // email" divider and use a narrower button.
    divider: { type: Boolean, default: true },
    buttonWidth: { type: Number, default: 320 },
    // A guest upgraded in place keeps their account id (and images):
    // stay on the page they were looking at instead of leaving it.
    stayOnUpgrade: { type: Boolean, default: false }
  },
  data() {
    return {
      errorMessage: "",
      busy: false,
      waiting: false,
      waitingTimer: null,
      rendered: false
    };
  },
  computed: {
    googleConfig() {
      // Runtime config, not a build-time env: ONE frontend bundle serves
      // every app, so the client id must come from the current site.
      const config = this.$store.state.publicConfig;
      return config && config.google_auth ? config.google_auth : null;
    },
    clientId() {
      return this.googleConfig && this.googleConfig.client_id
        ? this.googleConfig.client_id
        : "";
    }
  },
  watch: {
    // /public-config loads in parallel with the page; render whenever the
    // client id arrives (mounted alone would race it on a fresh load).
    clientId() {
      this.setup();
    }
  },
  mounted() {
    this.setup();
  },
  beforeDestroy() {
    clearTimeout(this.waitingTimer);
  },
  methods: {
    async setup() {
      if (!this.clientId || this.rendered) return;
      this.rendered = true;
      try {
        await loadGis();
      } catch (e) {
        // GIS blocked (adblock etc.) - the email form below still works.
        return;
      }
      // The v-if host may have appeared in this same tick.
      await this.$nextTick();
      window.google.accounts.id.initialize({
        client_id: this.clientId,
        callback: this.onCredential,
        // One account chooser per action; auto_select off = no silent logins.
        auto_select: false,
        cancel_on_tap_outside: true
      });
      if (this.$refs.buttonHost) {
        window.google.accounts.id.renderButton(this.$refs.buttonHost, {
          theme: "outline",
          size: "large",
          width: this.buttonWidth,
          text: this.mode === "register" ? "signup_with" : "signin_with",
          logo_alignment: "left",
          // Pressed feedback; auto-clears if the popup is closed without
          // completing (no callback fires in that case).
          click_listener: () => {
            this.waiting = true;
            clearTimeout(this.waitingTimer);
            this.waitingTimer = setTimeout(() => {
              this.waiting = false;
            }, 20000);
          }
        });
      }
      const oneTapEnabled = this.googleConfig && this.googleConfig.one_tap;
      if (this.oneTap && oneTapEnabled && !oneTapShown) {
        oneTapShown = true; // once per page load, across route changes
        window.google.accounts.id.prompt();
      }
    },
    async onCredential(response) {
      if (this.busy || !response || !response.credential) return;
      this.busy = true;
      this.waiting = false;
      clearTimeout(this.waitingTimer);
      this.errorMessage = "";
      const before = this.$store.getters.currentUser;
      const guestId = before && before.is_anonymous ? before.id : null;
      try {
        await this.$store.dispatch("loginWithGoogle", {
          credential: response.credential,
          refToken: this.refToken || ""
        });
        this.$emit("authenticated");
        const after = this.$store.getters.currentUser;
        if (this.stayOnUpgrade && guestId && after && after.id === guestId) {
          return;
        }
        // replace, not push: the auth page must not stay in history, or
        // the first Back press after signing in re-renders the form
        // (looks like a logout).
        this.$router
          .replace(takeAuthNext(this.$route.query) || "/")
          .catch(() => {});
      } catch (error) {
        const detail =
          error && error.response && error.response.data
            ? error.response.data.detail
            : null;
        this.errorMessage =
          typeof detail === "string"
            ? detail
            : "Google sign-in failed. Please try again or use email.";
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>

<style scoped>
.google-auth {
  margin-bottom: 4px;
}
.google-auth__button {
  display: flex;
  justify-content: center;
  min-height: 44px;
  /* Keeps the GIS iframe legible on dark app themes. */
  color-scheme: light;
}
.google-auth__status {
  margin-top: 6px;
  font-size: 0.8rem;
  text-align: center;
  color: var(--text-secondary, #64748b);
}

.google-auth__divider {
  display: flex;
  align-items: center;
  margin: 14px 0 6px;
  color: var(--text-secondary, rgba(128, 128, 128, 0.85));
  font-size: 12.5px;
}
.google-auth__divider::before,
.google-auth__divider::after {
  content: "";
  flex: 1;
  border-top: 1px solid rgba(128, 128, 128, 0.3);
}
.google-auth__divider span {
  padding: 0 10px;
}
</style>
